import { useCallback, useEffect, useState } from "react";
import ops from "../../styles/opsConsole.module.css";
import css from "./Vendors.module.css";
import VendorSection from "./VendorSection";

/**
 * WHAT A VENDOR ACTUALLY DOES, beside who they are.
 *
 * The vendor page was one scroll of profile cards — tax, banking, agreement —
 * while everything that matters day to day (what we owe them, what sold, who
 * bought it) lived on four other screens you had to filter by hand.
 *
 * Each section loads only when opened. A vendor with a thousand subscriptions
 * should not make opening their profile slow, and most visits want one of
 * these, not all four.
 *
 * Orders, subscriptions and customers key off the vendor's PRICE ROWS, because
 * after the multi-vendor offer split that is the only link that survives: an
 * order belongs to a vendor because it was sold on their offer.
 */

const money = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const date = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? "—" : t.toLocaleDateString();
};

const COLUMNS = {
  payouts: [
    ["id", "#", (r) => r.id],
    ["order", "Order", (r) => r.order_id ?? "—"],
    ["status", "Status", (r) => r.status || "—"],
    ["amount", "Amount", (r) => money(r.amount)],
    ["payment", "Payment", (r) => r.payment_status || "—"],
    [
      "po",
      "Books PO",
      (r) =>
        r.books_po?.link ? (
          <a href={r.books_po.link} target="_blank" rel="noreferrer">
            {r.books_po.number || "Open"}
          </a>
        ) : r.books_po?.reason ? (
          <span title={r.books_po.reason}>{r.books_po.status || "not raised"}</span>
        ) : (
          "—"
        ),
    ],
    ["created", "Created", (r) => date(r.created_at)],
  ],
  orders: [
    ["id", "#", (r) => r.id],
    ["plan", "Plan", (r) => r.plan || "—"],
    ["workspace", "Workspace", (r) => r.work_id ?? "—"],
    ["status", "Status", (r) => r.status || "—"],
    ["amount", "Amount", (r) => money(r.amount)],
    ["created", "Placed", (r) => date(r.created_at)],
  ],
  subscriptions: [
    ["id", "#", (r) => r.id],
    ["product", "Product", (r) => r.product || "—"],
    ["plan", "Plan", (r) => r.plan || "—"],
    ["licences", "Seats", (r) => r.licences ?? "—"],
    ["status", "Status", (r) => r.status || "—"],
    ["amount", "Amount", (r) => money(r.amount)],
    ["renews", "Renews", (r) => date(r.renews)],
  ],
  customers: [
    ["name", "Customer", (r) => r.name],
    ["active", "Active subs", (r) => r.active],
    ["total", "All subs", (r) => r.total],
    ["spend", "Spend", (r) => money(r.spend)],
  ],
};

const EMPTY = {
  payouts: "Nothing has been owed to this vendor yet.",
  orders: "No order has ever been placed on their prices.",
  subscriptions: "Nobody is subscribed on their prices.",
  customers: "No workspace holds a subscription of theirs.",
};

const SUMMARY = {
  payouts: "What we owe them, and what has been paid",
  orders: "Every order placed on their prices",
  subscriptions: "Live and past subscriptions sold on their prices",
  customers: "Workspaces that buy from them",
};

/**
 * Controlled from the page (open/onToggle) so "expand all" reaches it too.
 * Still fetched on FIRST open only; reopening shows what was already fetched.
 */
export default function VendorActivity({ vendorId, scope, title, get, open, onToggle }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return get(`/admin/vendors/${vendorId}/activity?scope=${scope}`)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || "Could not load that section."))
      .finally(() => setLoading(false));
  }, [get, vendorId, scope]);

  useEffect(() => {
    if (open && rows === null && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cols = COLUMNS[scope] || [];
  const count =
    rows === null ? null : `${rows.length}${rows.length === 200 ? "+" : ""}`;

  return (
    <VendorSection
      id={scope}
      icon={scope}
      title={title}
      summary={SUMMARY[scope]}
      tone={rows && rows.length ? "info" : "neutral"}
      open={open}
      onToggle={onToggle}
      meta={
        count !== null ? (
          <span className={`${css.secPill} ${rows.length ? css.secPill_info : ""}`}>
            {count}
          </span>
        ) : (
          <span className={css.secPill}>loads when opened</span>
        )
      }
    >
      <div>
        {loading && <p className={css.secEmpty}>Loading…</p>}
        {error && (
          <p className={css.secEmpty} style={{ color: "var(--danger)" }}>
            {error}{" "}
            <button
              type="button"
              onClick={load}
              className={css.secBarBtn}
            >
              Try again
            </button>
          </p>
        )}
        {!loading && !error && rows !== null && rows.length === 0 && (
          <p className={css.secEmpty}>{EMPTY[scope]}</p>
        )}
        {!loading && !error && rows !== null && rows.length > 0 && (
          <div className={css.tableWrap}>
            <table className={ops.table}>
              <thead>
                <tr>
                  {cols.map(([k, label]) => (
                    <th key={k}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id ?? r.work_id ?? i}>
                    {cols.map(([k, , render]) => (
                      <td key={k}>{render(r)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </VendorSection>
  );
}
